import React, { useEffect, useState } from "react";
import { useApiData } from "../hooks/useApiData";
import "./PartnerLogos.css";

var IMAGE_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || "";

// Fixed parid values (from tbl_parameters) for the Facebook / Twitter /
// Instagram URL settings. Editing par_value for these rows in
// ManageSetting updates the site live; the parid itself only needs to
// change here if that row is ever deleted and recreated in the database.
var FACEBOOK_URL_PARID = 31;
var TWITTER_URL_PARID = 32;
var INSTAGRAM_URL_PARID = 33;
// ──────────────────────────────────────────────────────────────────────

function normalize(item) {
  var rawImage = item.image || "";
  var isAbsolute = rawImage.indexOf("http") === 0;
  var image;

  if (isAbsolute) {
    image = rawImage;
  } else {
    image = (IMAGE_BASE_URL + "/" + rawImage).replace(/([^:]\/)\/+/g, "$1");
  }

  var sortOrder = 0;
  if (item.sortOrder !== undefined && item.sortOrder !== null) {
    sortOrder = item.sortOrder;
  } else if (item.order !== undefined && item.order !== null) {
    sortOrder = item.order;
  }

  var status = 1;
  if (item.status !== undefined && item.status !== null) {
    status = item.status;
  }

  var name = item.name || item.title || "";
  var url = item.url || item.link || "#";

  return {
    id: item.id,
    name: name,
    image: image,
    url: url,
    sortOrder: Number(sortOrder),
    status: Number(status)
  };
}

function buildFallbackAvatarUrl(name) {
  return "https://ui-avatars.com/api/?name=" + encodeURIComponent(name) + "&background=00274C&color=fff&size=90";
}

// Accepts "https://x.com/abc", "twitter.com/abc", "www.instagram.com/abc"
// or "@abc" / "abc" and returns a safe absolute URL (or null if empty).
function normalizeSocialUrl(raw, platform) {
  if (!raw) return null;
  var v = String(raw).trim();
  if (!v) return null;

  // "@handle" or bare "handle" -> build a profile URL
  if (v.indexOf(".") === -1 && v.indexOf("/") === -1) {
    var handle = v.replace(/^@/, "");
    if (platform === "twitter") return "https://twitter.com/" + handle;
    if (platform === "instagram") return "https://www.instagram.com/" + handle;
    if (platform === "facebook") return "https://www.facebook.com/" + handle;
    return null;
  }

  if (!/^https?:\/\//i.test(v)) v = "https://" + v;
  return v;
}

// Base URL for the public API (same idea as IMAGE_BASE_URL above).
// Leave as "" if your frontend already proxies /api/* to the backend
// (typical create-react-app "proxy" setup in dev); set
// REACT_APP_API_URL for production if the API lives on a different host.
var API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Fetches a single setting's par_value from tbl_parameters via
// GET /parameters/:parid — the parid itself is fixed (see
// FACEBOOK_URL_PARID etc. above), but the value it returns is always
// read live from the backend, so editing it in ManageSetting updates the
// site without any code change or redeploy.
// Returns { value, loading }. `value` is null while loading, on error,
// or if the row has no par_value yet (empty string / NULL) — callers use
// that to decide whether the tab should be enabled or disabled.
function useParameter(parid) {
  var valueState = useState(null);
  var value = valueState[0];
  var setValue = valueState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  useEffect(
    function () {
      var cancelled = false;
      setLoading(true);

      fetch(API_BASE_URL + "/parameters/" + parid, { cache: "no-store" })
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          if (cancelled) return;

          // par_value must be a real non-empty string to count as "set".
          // Empty string, null, undefined all resolve to null here.
          var raw = json && json.value ? String(json.value).trim() : "";
          setValue(raw.length > 0 ? raw : null);
        })
        .catch(function (err) {
          if (cancelled) return;
          console.log("parameter " + parid + " fetch error:", err);
          setValue(null);
        })
        .finally(function () {
          if (!cancelled) setLoading(false);
        });

      return function () {
        cancelled = true;
      };
    },
    [parid]
  );

  return { value: value, loading: loading };
}

// Inline X logo (no Font Awesome dependency, so it never shows as an
// empty box when the icon font version doesn't have the glyph).
function XLogo(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={props.size || 48}
      height={props.size || 48}
      fill={props.color || "#000"}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// ── Social media tabs (Facebook / Twitter / Instagram) ──────────────────
var SOCIAL_TABS = [
  { id: "facebook", label: "Facebook" },
  { id: "twitter", label: "Twitter" },
  { id: "instagram", label: "Instagram" }
];

function SocialMediaTabs() {
  var state = useState(null); // null until we know which tabs are enabled
  var activeTab = state[0];
  var setActiveTab = state[1];

  var fbLoadedState = useState(false);
  var facebookLoaded = fbLoadedState[0];
  var setFacebookLoaded = fbLoadedState[1];

  // Each social URL is fetched live from the backend by its fixed parid.
  // value is null while loading, on error, or when par_value is empty —
  // in all those cases the corresponding tab is disabled.
  var fbParam = useParameter(FACEBOOK_URL_PARID);
  var twParam = useParameter(TWITTER_URL_PARID);
  var igParam = useParameter(INSTAGRAM_URL_PARID);

  // Normalized, absolute URLs (null when the par_value is empty).
  var facebookUrl = normalizeSocialUrl(fbParam.value, "facebook");
  var twitterUrl = normalizeSocialUrl(twParam.value, "twitter");
  var instagramUrl = normalizeSocialUrl(igParam.value, "instagram");

  var urlsByTab = {
    facebook: facebookUrl,
    twitter: twitterUrl,
    instagram: instagramUrl
  };

  var loadingByTab = {
    facebook: fbParam.loading,
    twitter: twParam.loading,
    instagram: igParam.loading
  };

  var anyLoading = fbParam.loading || twParam.loading || igParam.loading;

  // A tab is "enabled" only once its param has finished loading AND has a
  // real, non-empty par_value.
  var enabledTabs = SOCIAL_TABS.filter(function (tab) {
    return !loadingByTab[tab.id] && !!urlsByTab[tab.id];
  });

  var enabledIds = enabledTabs.map(function (t) {
    return t.id;
  });

  // Once loading settles, make sure activeTab points at an enabled tab.
  // If the currently selected tab isn't enabled (or nothing was selected
  // yet), fall back to the first enabled tab.
  useEffect(
    function () {
      if (anyLoading) return;

      if (enabledIds.length === 0) {
        setActiveTab(null);
        return;
      }

      if (!activeTab || enabledIds.indexOf(activeTab) === -1) {
        setActiveTab(enabledIds[0]);
      }
    },
    [anyLoading, enabledIds.join(",")]
  );

  function renderFacebookPanel() {
    var src =
      "https://www.facebook.com/plugins/page.php?href=" +
      encodeURIComponent(facebookUrl) +
      "&tabs=timeline&width=340&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false";

    return (
      <div className="social-tab-panel">
        {!facebookLoaded ? (
          <div className="social-embed-skeleton">
            <div className="skeleton social-embed-skeleton-header"></div>
            <div className="skeleton social-embed-skeleton-row"></div>
            <div className="skeleton social-embed-skeleton-row"></div>
            <div className="skeleton social-embed-skeleton-block"></div>
          </div>
        ) : null}

        <iframe
          key="facebook-embed"
          title="Facebook Page"
          src={src}
          className="social-embed-frame"
          style={{
            border: "none",
            overflow: "hidden",
            display: facebookLoaded ? "block" : "none"
          }}
          scrolling="no"
          frameBorder="0"
          allowFullScreen={true}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          onLoad={function () {
            setFacebookLoaded(true);
          }}
        ></iframe>

        <a
          href={facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="social-fallback-link"
        >
          Not loading? Visit our Facebook Page
        </a>
      </div>
    );
  }

  function renderTwitterPanel() {
    return (
      <div className="social-tab-panel social-instagram-panel">
        <div className="social-instagram-card">
          <div className="social-instagram-icon" style={{ lineHeight: 0 }}>
            <XLogo size={48} color="#000" />
          </div>
          <p className="social-instagram-text">
            Follow MKCG on Twitter / X for updates and announcements.
          </p>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="social-instagram-btn"
            style={{ background: "#2ba7e1" }}
          >
            View on Twitter
          </a>
        </div>
      </div>
    );
  }

  function renderInstagramPanel() {
    return (
      <div className="social-tab-panel social-instagram-panel">
        <div className="social-instagram-card">
          <i className="fa fa-instagram social-instagram-icon"></i>
          <p className="social-instagram-text">
            Follow MKCG on Instagram for photos and campus updates.
          </p>
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="social-instagram-btn"
          >
            View on Instagram
          </a>
        </div>
      </div>
    );
  }

  // Still waiting on the parameters to load — render nothing yet rather
  // than flashing the "not available" message before we actually know.
  if (anyLoading) {
    return null;
  }

  // Nothing enabled at all (all three par_value empty) — don't render an
  // empty/broken tab strip, show a small placeholder instead.
  if (enabledTabs.length === 0) {
    return (
      <div className="social-tabs-wrap">
        <p className="social-tabs-empty">Social media links are not available right now.</p>
      </div>
    );
  }

  return (
    <div className="social-tabs-wrap">
      <div className="social-tabs" role="tablist">
        {enabledTabs.map(function (tab) {
          var isActive = activeTab === tab.id;

          var className = "social-tab-btn " + tab.id;
          if (isActive) className += " active";

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={className}
              onClick={function () {
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="social-tab-content">
        {activeTab === "facebook" ? renderFacebookPanel() : null}
        {activeTab === "twitter" ? renderTwitterPanel() : null}
        {activeTab === "instagram" ? renderInstagramPanel() : null}
      </div>
    </div>
  );
}

function PartnerLogos() {
  var apiResult = useApiData("links");
  var data = apiResult.data;
  var loading = apiResult.loading;

  var rawList = data || [];
  var normalized = [];
  var i;

  for (i = 0; i < rawList.length; i++) {
    normalized.push(normalize(rawList[i]));
  }

  var activeLogos = [];
  for (i = 0; i < normalized.length; i++) {
    if (normalized[i].status === 1) {
      activeLogos.push(normalized[i]);
    }
  }

  activeLogos.sort(function (a, b) {
    return a.sortOrder - b.sortOrder;
  });

  var loopLogos = [];
  if (activeLogos.length > 0) {
    loopLogos = activeLogos.concat(activeLogos);
  }

  var skeletonItems = [0, 1, 2, 3, 4, 5];

  function handleImgError(e, logoName) {
    e.target.onerror = null;
    e.target.src = buildFallbackAvatarUrl(logoName);
  }

  function renderSkeleton() {
    return skeletonItems.map(function (n) {
      return (
        <div key={n} className="partner-logo-item">
          <div className="skeleton partner-logo-skeleton-box"></div>
          <div className="skeleton partner-logo-skeleton-text"></div>
        </div>
      );
    });
  }

  function renderLogos() {
    return loopLogos.map(function (logo, idx) {
      var itemKey = logo.id + "_" + idx;
      return (
        <a key={itemKey} href={logo.url} target="_blank" rel="noopener noreferrer" className="partner-logo-item" title={logo.name}>
          <div className="partner-logo-img-wrap">
            <img
              src={logo.image}
              alt={logo.name}
              loading="lazy"
              onError={function (e) {
                handleImgError(e, logo.name);
              }}
            />
          </div>
          <span className="partner-logo-name">{logo.name}</span>
        </a>
      );
    });
  }

  var trackContent = null;
  if (loading) {
    trackContent = (
      <div className="partner-logos-track partner-logos-static">
        {renderSkeleton()}
      </div>
    );
  } else {
    trackContent = (
      <div className="partner-logos-track">
        {renderLogos()}
      </div>
    );
  }

  return (
    <section className="partner-logos-section" style={{ padding: "0" }}>
      <div className="container">
        <div className="affiliations">
          <p className="section-eyebrow">Our Affiliations</p>
          <h2 className=" section-title">Recognitions and Affiliated Bodies</h2>
          <div className="border1"></div>
          <div className="partner-logos-viewport">
            {trackContent}
          </div>
        </div>
        <div className="social">
          <SocialMediaTabs />
        </div>
      </div>
    </section>
  );
}

export default PartnerLogos;
export { PartnerLogos };
